/**
 * CalendarView — /#/calendar
 *
 * Marketing/sales calendar of events tied to KPIs.
 * Roles: All
 *
 * - Month grid showing marketing events + sales milestones
 * - Each event has KPI tags
 * - Click event → modal with details + linked hooks/templates
 *
 * Seed data inline.
 */

import { useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";

const BLACK = "#0B0B0F";
const BG = "#FAFAF7";

// ── Event types ───────────────────────────────────────────────────────────────

type EventType = "webinar" | "tradeshow" | "content" | "qbr" | "kickoff" | "review";

interface CalEvent {
  id: string;
  title: string;
  type: EventType;
  day: number; // day of month
  kpiTags: string[];
  description: string;
  linkedHooks?: string[];
  linkedTemplates?: string[];
}

const EVENT_META: Record<EventType, { color: string; bg: string; label: string }> = {
  webinar:    { color: "#0B0B0F", bg: "#FAFAF7",   label: "Webinar" },
  tradeshow:  { color: "#0B0B0F", bg: "#FDF4FF",   label: "Trade show" },
  content:    { color: "#2A2A2E", bg: "#FAFAF7",   label: "Content drop" },
  qbr:        { color: "#C2410C", bg: "#FFF7ED",   label: "QBR" },
  kickoff:    { color: "#15803D", bg: "#F0FDF4",   label: "Kickoff" },
  review:     { color: "#374151", bg: "#F9FAFB",   label: "Review" },
};

// Seed for June 2025
const EVENTS: CalEvent[] = [
  {
    id: "e1", title: "Mfg Tech Webinar — Predictive Maintenance ROI", type: "webinar", day: 3,
    kpiTags: ["MQL target +25", "SQL target +12"],
    description: "Live webinar targeting plant managers and maintenance leads at Tier 1/2 manufacturers. Joint session with Mi6 Group + Attilius product team.",
    linkedHooks: ["Predictive Maintenance Value Prop", "Downtime Cost Calculator angle"],
    linkedTemplates: ["Post-webinar follow-up cadence"],
  },
  {
    id: "e2", title: "IMTS Chicago — Pre-show outreach launch", type: "tradeshow", day: 5,
    kpiTags: ["MQL target +40", "Meeting target +15"],
    description: "Pre-show outreach to registered attendees in our target verticals. BDR priority: engineering managers at precision machining companies.",
    linkedHooks: ["Trade show booth hook", "IMTS engagement play"],
    linkedTemplates: ["Trade show pre-outreach sequence"],
  },
  {
    id: "e3", title: "Q2 2025 QBR — Team review", type: "qbr", day: 9,
    kpiTags: ["Attainment review", "Q3 quota confirmation"],
    description: "Quarterly business review. All AEs present their pipeline health, stage velocity, and Q3 game plan. Manager + Executive attendance required.",
    linkedHooks: [],
    linkedTemplates: ["QBR deck template"],
  },
  {
    id: "e4", title: "Medical Device Compliance Guide — Content drop", type: "content", day: 11,
    kpiTags: ["MQL target +18", "Organic traffic +30%"],
    description: "In-depth guide: 'Navigating 21 CFR Part 11 for Medical Device Manufacturers in 2025.' Gated content to capture quality leads from med-device vertical.",
    linkedHooks: ["Compliance risk hook", "Audit cost angle"],
    linkedTemplates: ["Med device nurture sequence"],
  },
  {
    id: "e5", title: "Automotive OEM Campaign kickoff", type: "kickoff", day: 14,
    kpiTags: ["Campaign MQL target +60", "SQL target +22"],
    description: "Summer outbound campaign targeting Tier 1 automotive OEM and Tier 2 supplier contacts. Focus on supply chain resilience and connected manufacturing.",
    linkedHooks: ["Automotive supply chain hook", "Connected factory ROI"],
    linkedTemplates: ["Automotive outbound cadence"],
  },
  {
    id: "e6", title: "IoT Podcast Episode — 'Connected Factory 2025'", type: "content", day: 17,
    kpiTags: ["Brand reach +15K listeners", "MQL target +8"],
    description: "Mi6 Group featured on the Industrial IoT Podcast. Topics: sensor data monetization, edge computing ROI, connectivity security. Bryan Attilius guest.",
    linkedHooks: ["IoT edge ROI hook", "Security objection reframe"],
    linkedTemplates: [],
  },
  {
    id: "e7", title: "Pipeline review — Week of Jun 16", type: "review", day: 18,
    kpiTags: ["Coverage target 3×", "At-risk deals review"],
    description: "Weekly pipeline review for manager + AEs. Focus on deals stuck in CP3/CP4 for 20+ days. Top 5 at-risk deals get a coaching segment.",
    linkedHooks: [],
    linkedTemplates: ["Deal rescue sequence"],
  },
  {
    id: "e8", title: "Aerospace & Defense Webinar — Design-to-Production", type: "webinar", day: 20,
    kpiTags: ["MQL target +20", "SQL target +9"],
    description: "Targeted at engineering leads at prime contractors and Tier 1 defense suppliers. Use case: reducing re-work cost in complex assembly environments.",
    linkedHooks: ["Aerospace quality hook", "Re-work cost angle"],
    linkedTemplates: ["Aerospace follow-up cadence"],
  },
  {
    id: "e9", title: "Salesforce Sync — CRM hygiene blitz", type: "review", day: 23,
    kpiTags: ["Stale accounts < 10%", "Stage accuracy target 95%"],
    description: "Team-wide CRM hygiene session. Update stage, next step, and close date for all accounts active in the last 60 days.",
    linkedHooks: [],
    linkedTemplates: [],
  },
  {
    id: "e10", title: "Building Materials Summer Promo — Email blast", type: "content", day: 25,
    kpiTags: ["Open rate target 28%", "MQL target +12"],
    description: "Seasonal campaign email to building materials distributors and contractors. Topic: leveraging Q3 demand surge for upsell and cross-sell.",
    linkedHooks: ["Seasonal demand hook", "Contractor network play"],
    linkedTemplates: ["Building materials email series"],
  },
  {
    id: "e11", title: "Q3 2025 Kickoff — All-hands", type: "kickoff", day: 28,
    kpiTags: ["Quota confirmed", "Campaign targets set"],
    description: "Q3 kickoff all-hands for full revenue team. BDR, AE, SE, Marketing, Management. Territory assignments, quota commitments, and top campaign priorities.",
    linkedHooks: [],
    linkedTemplates: ["Q3 kickoff agenda"],
  },
  {
    id: "e12", title: "Helix Score Demo Day — Pilot accounts", type: "webinar", day: 30,
    kpiTags: ["Demo target 6 accounts", "SQL target +6"],
    description: "Live demonstration of the Helix M-PACT scoring model with 6 target pilot accounts. SE + AE co-present. Outcome: POC agreement or next discovery call.",
    linkedHooks: ["Helix score opener", "M-PACT maturity hook"],
    linkedTemplates: ["Post-demo follow-up sequence"],
  },
];

// ── Calendar grid helpers ─────────────────────────────────────────────────────

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay(); // 0=Sun
}

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAY_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

// ── Event chip ────────────────────────────────────────────────────────────────

function EventChip({ event, onClick }: { event: CalEvent; onClick: () => void }) {
  const m = EVENT_META[event.type];
  return (
    <button
      onClick={onClick}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        background: m.bg,
        color: m.color,
        border: "none",
        borderLeft: `2px solid ${m.color}`,
        borderRadius: "0 4px 4px 0",
        padding: "2px 5px",
        fontSize: 10,
        fontWeight: 600,
        cursor: "pointer",
        marginBottom: 2,
        lineHeight: 1.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {event.title}
    </button>
  );
}

// ── Event modal ───────────────────────────────────────────────────────────────

function EventModal({ event, onClose }: { event: CalEvent; onClose: () => void }) {
  const m = EVENT_META[event.type];
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#FFFFFF", borderRadius: 16, padding: 28,
          maxWidth: 520, width: "100%", boxShadow: "0 20px 60px rgba(15,23,42,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <span style={{ background: m.bg, color: m.color, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 99 }}>
            {m.label}
          </span>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 18, color: "#8E8E94", lineHeight: 1 }}>×</button>
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: BLACK, margin: "0 0 8px" }}>{event.title}</h2>
        <div style={{ fontSize: 12, color: "#6B6B70", lineHeight: 1.6, marginBottom: 16 }}>{event.description}</div>

        {event.kpiTags.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#8E8E94", marginBottom: 6 }}>KPI targets</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {event.kpiTags.map((t) => (
                <span key={t} style={{ background: "#F0FDF4", color: "#15803D", fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 99 }}>{t}</span>
              ))}
            </div>
          </div>
        )}

        {event.linkedHooks && event.linkedHooks.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#8E8E94", marginBottom: 6 }}>Linked hooks</div>
            {event.linkedHooks.map((h) => (
              <a key={h} href="/#/marketing/hooks" style={{ display: "block", fontSize: 12, color: "#0B0B0F", marginBottom: 3, textDecoration: "none" }}>→ {h}</a>
            ))}
          </div>
        )}

        {event.linkedTemplates && event.linkedTemplates.length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#8E8E94", marginBottom: 6 }}>Linked templates</div>
            {event.linkedTemplates.map((t) => (
              <a key={t} href="/#/templates" style={{ display: "block", fontSize: 12, color: "#0B0B0F", marginBottom: 3, textDecoration: "none" }}>→ {t}</a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

export default function CalendarView() {
  const [selectedEvent, setSelectedEvent] = useState<CalEvent | null>(null);

  // Fixed to June 2025 (seed data month)
  const year = 2025;
  const month = 5; // June (0-indexed)

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  // Build grid cells (6 weeks × 7 days)
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  const eventsByDay = new Map<number, CalEvent[]>();
  EVENTS.forEach((e) => {
    const arr = eventsByDay.get(e.day) || [];
    arr.push(e);
    eventsByDay.set(e.day, arr);
  });

  return (
    <OperatorShell breadcrumbs={[{ label: "Calendar" }]}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <h1 style={{ fontSize: 26, fontWeight: 900, color: BLACK, margin: 0, letterSpacing: "-0.01em" }}>
                {MONTH_NAMES[month]} {year}
              </h1>
              <span
                style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
                  padding: "2px 7px", borderRadius: 99, background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A",
                }}
              >
                Seed data
              </span>
            </div>
            <div style={{ fontSize: 14, color: "#6B6B70" }}>Marketing events and sales milestones with KPI targets. Click any event for details.</div>
          </div>
          {/* Legend */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {(Object.entries(EVENT_META) as [EventType, typeof EVENT_META[EventType]][]).map(([type, m]) => (
              <span key={type} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: "#6B6B70" }}>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: m.color }} />
                {m.label}
              </span>
            ))}
          </div>
        </div>

        {/* Calendar grid */}
        <div style={{ background: "#FFFFFF", border: "1px solid #E5E5E7", borderRadius: 12, overflow: "hidden" }}>
          {/* Day labels */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "1px solid #E5E5E7" }}>
            {DAY_LABELS.map((d) => (
              <div key={d} style={{ padding: "8px 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#8E8E94", textAlign: "center" }}>
                {d}
              </div>
            ))}
          </div>

          {/* Weeks */}
          {weeks.map((week, wi) => (
            <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: wi < weeks.length - 1 ? "1px solid #E5E5E7" : "none" }}>
              {week.map((day, di) => {
                const dayEvents = day ? (eventsByDay.get(day) || []) : [];
                const today = day === 16; // simulated "today"
                return (
                  <div
                    key={di}
                    style={{
                      minHeight: 90,
                      padding: "6px 8px",
                      borderRight: di < 6 ? "1px solid #E5E5E7" : "none",
                      background: day ? "#FFFFFF" : "#FAFAF7",
                    }}
                  >
                    {day && (
                      <>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: today ? 800 : 500,
                            color: today ? "#FFFFFF" : BLACK,
                            marginBottom: 4,
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: today ? BLACK : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {day}
                        </div>
                        {dayEvents.map((ev) => (
                          <EventChip key={ev.id} event={ev} onClick={() => setSelectedEvent(ev)} />
                        ))}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {selectedEvent && <EventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />}
    </OperatorShell>
  );
}
